import React, { useEffect, useMemo } from 'react';
import _get from 'lodash/get';
import { FilterGroupingEnum, useDepartmentIdsQuery } from 'graphql-common';
import { Option } from '@lib/interfaces/form';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import FieldNames from '@lib/enums/fieldNames/interventionFieldNames';

export default function useDepartmentLinksPrefill({
  formRef,
  interventionId,
  initialDepartmentLinksRef,
  skip = false,
}: {
  formRef: FormRefType;
  interventionId?: string;
  initialDepartmentLinksRef?: React.MutableRefObject<Option[]>;
  skip?: boolean;
}) {
  const { data, loading, error } = useDepartmentIdsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      onlyIds: true,
      filters: [
        {
          departmentInterventionsInterventionId: {
            grouping: FilterGroupingEnum.And,
            predicate: { eq: interventionId || '' },
          },
        },
      ],
    },
    skip: !interventionId || skip,
  });

  const initialDepartmentLinks: Option[] = useMemo(() => {
    const ids = _get(data, 'data.collection', []) as Array<{ id: string }>;
    return ids.map(({ id }) => ({ value: id }));
  }, [data]);

  useEffect(() => {
    if (!interventionId || !data) return;
    if (initialDepartmentLinksRef) {
      // eslint-disable-next-line no-param-reassign
      initialDepartmentLinksRef.current = initialDepartmentLinks;
    }
    if (formRef.current) {
      formRef.current.setValue(
        FieldNames.DepartmentLinks,
        initialDepartmentLinks,
        { shouldDirty: false, shouldTouch: false, shouldValidate: false },
      );
    }
  }, [
    data,
    formRef,
    interventionId,
    initialDepartmentLinks,
    initialDepartmentLinksRef,
  ]);

  return {
    loading: Boolean(interventionId) && !skip && !error && (loading || !data),
  };
}
